import type { FC } from 'react';
import React, { useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	NEXT_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
	UNIVERSAL_PREV_COMMENT_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { fg } from '@confluence/feature-gating';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { InlineHeaderButton, NavigationContainer } from './styled-components.tsx';

const i18n = defineMessages({
	nextCommentLabel: {
		id: 'inline-comments-common.next-comment-label',
		description:
			'The text is used as the accessible label for the navigation button that moves focus to the next inline comment on a Confluence page, shown in the comment navigation controls.',
		defaultMessage: 'Next comment',
	},
	previousCommentLabel: {
		id: 'inline-comments-common.previous-comment-label',
		description: 'Label displayed for the Previous comment',
		defaultMessage: 'Previous comment',
	},
});

type CommentNavigationProps = {
	isSSRFocusedComment?: boolean;
	goToNextInlineComment: (arg?: boolean) => void;
	goToPrevInlineComment: (arg?: boolean) => void;
};

export const CommentNavigation: FC<CommentNavigationProps> = ({
	isSSRFocusedComment,
	goToNextInlineComment,
	goToPrevInlineComment,
}) => {
	const { formatMessage } = useIntl();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();
	const pageClass = usePageClass();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const prevCommentShortcutStrings = useShortcutStrings({
		shortcut:
			isLiveEditMode || isOnEditRoute ? UNIVERSAL_PREV_COMMENT_SHORTCUT : PREV_COMMENT_SHORTCUT,
	});
	const nextCommentShortcutStrings = useShortcutStrings({
		shortcut:
			isLiveEditMode || isOnEditRoute ? UNIVERSAL_NEXT_COMMENT_SHORTCUT : NEXT_COMMENT_SHORTCUT,
	});

	const renderServerPlaceholder = useRenderServerPlaceholder();

	const noop = () => {};

	const isPageOrDoc = pageClass === 'renderer' || pageClass === 'editor';

	return (
		<NavigationContainer
			data-testid="comment-navigation-container"
			shouldHaveButtonGap={fg('confluence_frontend_content_wrapper')}
		>
			<Tooltip
				content={<FormattedMessage {...i18n.previousCommentLabel} />}
				shortcut={prevCommentShortcutStrings}
				hideTooltipOnClick
				position="top"
			>
				<InlineHeaderButton
					isSSRFocusedComment={isSSRFocusedComment}
					data-cy="inline-nav-previous"
					testId="comment-navigation-prev"
					iconBefore={
						<ChevronUpIcon
							label={formatMessage(i18n.previousCommentLabel)}
							color={token('color.icon.subtle')}
							spacing="spacious"
							size="small"
						/>
					}
					onClick={
						!isSSRFocusedComment
							? () => {
									selectInlineCommentCompoundExperience.selectAnnotation.start({
										pageClass,
										entryPoint: 'comment-navigation',
									});
									if (isPageOrDoc && fg('confluence_frontend_view_inline_comment_arrow_nav')) {
										experienceTracker.start({
											name: VIEW_INLINE_COMMENT_EXPERIENCE,
											timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
											attributes: {
												...getCommentsDataManagerExperimentAttributes(),
												entryPoint: 'inline-comments-common/CommentNavigation',
												pageClass,
											},
										});
									}
									selectInlineComment.debug('Navigating to previous comment via button.');
									return goToPrevInlineComment();
								}
							: noop
					}
				/>
			</Tooltip>
			<Tooltip
				content={<FormattedMessage {...i18n.nextCommentLabel} />}
				shortcut={nextCommentShortcutStrings}
				hideTooltipOnClick
				position="top"
			>
				<InlineHeaderButton
					isSSRFocusedComment={isSSRFocusedComment}
					data-cy="inline-nav-next"
					testId="comment-navigation-next"
					iconBefore={
						<ChevronDownIcon
							label={formatMessage(i18n.nextCommentLabel)}
							color={token('color.icon.subtle')}
							spacing="spacious"
							size="small"
						/>
					}
					onClick={
						!isSSRFocusedComment
							? () => {
									selectInlineCommentCompoundExperience.selectAnnotation.start({
										pageClass,
										entryPoint: 'comment-navigation',
									});
									if (isPageOrDoc && fg('confluence_frontend_view_inline_comment_arrow_nav')) {
										experienceTracker.start({
											name: VIEW_INLINE_COMMENT_EXPERIENCE,
											timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
											attributes: {
												...getCommentsDataManagerExperimentAttributes(),
												entryPoint: 'inline-comments-common/CommentNavigation',
												pageClass,
											},
										});
									}
									selectInlineComment.debug('Navigating to next comment via button.');
									return goToNextInlineComment();
								}
							: noop
					}
				/>
			</Tooltip>
			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
			{renderServerPlaceholder ? <span id="inline-comment-index" /> : null}
		</NavigationContainer>
	);
};
